import React, { lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { SettingsProvider } from "./contexts/SettingsContext";
import { TimerProvider } from "./contexts/TimerContext";
import { ProfileProvider } from "./contexts/ProfileContext";
import { FilterProvider } from "./contexts/FilterContext";
import { OnboardingProvider } from "./contexts/OnboardingContext";
import { PlanProvider } from "./contexts/PlanContext";
// ⭐ Language + Currency (global, persisted, auto-detects country)
import { LocaleProvider } from "./contexts/LocaleContext";

/* ═══════════════════════════════════════════════════════════════
   ⭐ CRITICAL PATH — keep these eager (needed on every page)
   ═══════════════════════════════════════════════════════════════ */
import ProtectedRoute from "./components/Auth/ProtectedRoute";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer/Footer";
import GlobalToaster from "./components/GlobalToaster";
import usePaymentNotifications from "./hooks/usePaymentNotifications";
import { useTheme } from "./hooks/useTheme";
import ApplyPage from "./components/ApplyPage";
import TrackApplication from "./pages/TrackApplication";

/* ═══════════════════════════════════════════════════════════════
   ⭐ LAZY-LOADED COMPONENTS — only downloaded when needed
   ═══════════════════════════════════════════════════════════════ */

// Heavy floating components (not needed on first paint)
const AIAssistant = lazy(() => import("./components/AIAssistant"));
const GlobalChatDock = lazy(() => import("./components/GlobalChatDock"));
const OnboardingModal = lazy(() => import("./components/OnboardingModal"));
const PremiumWelcomePopup = lazy(() => import("./components/PremiumWelcomePopup"));
const FloatingChat = lazy(() => import("./components/FloatingChat"));
const ShirkatFund = lazy(() => import("./pages/ShirkatFund"));

// Landing
const Landing = lazy(() => import("./pages/Landing"));

// Auth pages
const SignIn = lazy(() => import("./components/Auth/SignIn"));
const SignUp = lazy(() => import("./components/Auth/SignUp"));
const ResetPassword = lazy(() => import("./components/Auth/ResetPassword"));
const ResetPasswordVerify = lazy(() => import("./components/Auth/ResetPasswordVerify"));
const ResetPasswordUpdate = lazy(() => import("./components/Auth/ResetPasswordUpdate"));

// Core pages
const Home = lazy(() => import("./pages/Home"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const ChatPage = lazy(() => import("./pages/ChatPage"));
const HowItWorks = lazy(() => import("./components/LiveStudyMatching/HowItWorks"));
const Premium = lazy(() => import("./pages/Premium"));
const Services = lazy(() => import("./pages/Services"));
const UserServices = lazy(() => import("./pages/UserServices"));
const ServiceDetail = lazy(() => import("./pages/ServiceDetail"));
const StudyGroups = lazy(() => import("./pages/StudyGroups"));
const StudyPartnerRegister = lazy(() => import("./pages/StudyPartnerRegister"));
const StudyChat = lazy(() => import("./pages/StudyChat"));
const GroupDetail = lazy(() => import("./pages/GroupDetail"));
const Momento = lazy(() => import("./pages/Momento"));
const Wallet = lazy(() => import("./pages/Wallet"));

// Category pages
const Vehicles = lazy(() => import("./pages/Vehicles"));
const VehicleDetail = lazy(() => import("./pages/VehicleDetail"));
const BikeDetail = lazy(() => import("./pages/BikeDetail"));
const Mobiles = lazy(() => import("./pages/Mobiles"));
const MobileDetail = lazy(() => import("./pages/MobileDetail"));
const Property = lazy(() => import("./pages/Property"));
const MyListings = lazy(() => import("./pages/MyListings"));
const Electronics = lazy(() => import("./pages/Electronics"));
const ElectronicsDetail = lazy(() => import("./pages/ElectronicsDetail"));
const Feed = lazy(() => import("./pages/Feed"));
const PostAd = lazy(() => import("./pages/PostAd"));
const Orders = lazy(() => import("./pages/Orders"));
const Checkout = lazy(() => import("./pages/Checkout"));
const PropertyDetail = lazy(() => import("./pages/PropertyDetail"));
const ListingStatusPage = lazy(() => import("./pages/ListingStatusPage"));
const ToyDetail = lazy(() => import("./pages/ToyDetail"));
const MarketplaceChat = lazy(() => import("./pages/MarketplaceChat"));
const BoostCheckout = lazy(() => import("./pages/BoostCheckout"));

// Admin (heaviest — only admin ever loads these)
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminOnboarding = lazy(() => import("./pages/AdminOnboarding"));

// AI tools (heavy — only load when user opens them)
const AIImageGenerator = lazy(() => import("./pages/AIImageGenerator"));
const Library = lazy(() => import("./pages/Library"));
const RemoveBackground = lazy(() => import("./pages/RemoveBackground"));
const AIChat = lazy(() => import("./pages/AIChatpromt"));
const Img_GeneratorPro = lazy(() => import("./pages/Img_GeneratorPro"));

/* ═══════════════════════════════════════════════════════════════
   ADMIN EMAILS
   ═══════════════════════════════════════════════════════════════ */
const ADMIN_EMAILS = ["hasnainwebdeveloper1122@gmail.com"];

/* ═══════════════════════════════════════════════════════════════
   ⭐ SUSPENSE FALLBACK — skeleton loader (ApexDeal gold)
   ═══════════════════════════════════════════════════════════════ */
const PageLoader = () => (
  <div
    style={{
      minHeight: "60vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 40,
    }}
  >
    <div style={{ textAlign: "center" }}>
      <div
        style={{
          width: 40,
          height: 40,
          margin: "0 auto 12px",
          borderRadius: "50%",
          border: "3px solid rgba(201,169,97,0.2)",
          borderTopColor: "#C9A961",
          animation: "app-spin 0.8s linear infinite",
        }}
      />
      <p style={{ fontSize: 13, opacity: 0.6 }}>Loading…</p>
      <style>{`@keyframes app-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════════════════
   GLOBAL THEME STYLES — ApexDeal GOLD
   Light is PURE WHITE #FFFFFF · Dark = pure black #0A0A0A
   ═══════════════════════════════════════════════════════════════ */
const GlobalThemeStyles = () => (
  <style>{`
    html, body, #root {
      margin: 0;
      padding: 0;
      min-height: 100%;
      transition: background-color 0.35s ease, color 0.35s ease;
    }

    html.theme-dark,
    html.theme-dark body,
    html.theme-dark #root {
      background-color: #0A0A0A;
      color-scheme: dark;
    }

    html.theme-light,
    html.theme-light body,
    html.theme-light #root {
      background-color: #FFFFFF; /* ⭐ PURE WHITE */
      color-scheme: light;
    }

    /* ⭐ DEFAULT (no class yet) → LIGHT colors (pure white) */
    html:not(.theme-dark):not(.theme-light),
    html:not(.theme-dark):not(.theme-light) body,
    html:not(.theme-dark):not(.theme-light) #root {
      background-color: #FFFFFF; /* ⭐ PURE WHITE */
      color-scheme: light;
      color: #1A1613;
    }

    /* ⭐ Also seed light-mode CSS vars on the root before hydration */
    html:not(.theme-dark):not(.theme-light) {
      --nav-bg:           #FFFFFF;
      --nav-bg-2:         #F9FAFB;
      --nav-panel:        #FFFFFF;
      --nav-panel-2:      #F9FAFB;
      --nav-surface:      rgba(20,20,30,0.04);
      --nav-surface-2:    rgba(20,20,30,0.08);
      --nav-line:         rgba(20,20,30,0.08);
      --nav-line-str:     rgba(20,20,30,0.15);
      --nav-txt:          #1A1613;
      --nav-txt-soft:     rgba(26,22,19,0.62);
      --nav-txt-faint:    rgba(26,22,19,0.42);
      --nav-primary:      #8B6F2A;
      --nav-primary-2:    #A88B3D;
      --nav-primary-3:    #5C1A1B;
      --nav-primary-soft: rgba(139,111,42,0.10);
      --nav-primary-glow: rgba(139,111,42,0.35);
      --nav-shadow:       0 8px 24px -12px rgba(0,0,0,0.12);

      --app-bg:           #FFFFFF;
      --app-bg-2:         #F9FAFB;
      --app-txt:          #1A1613;
      --app-txt-soft:     rgba(26,22,19,0.62);
      --app-txt-faint:    rgba(26,22,19,0.42);
      --app-line:         rgba(20,20,30,0.08);
      --app-primary:      #8B6F2A;
      --app-primary-2:    #A88B3D;
    }

    html.theme-dark {
      --nav-bg:           #0A0A0A;
      --nav-bg-2:         #101010;
      --nav-panel:        #141414;
      --nav-panel-2:      #1A1A1A;
      --nav-surface:      rgba(242,210,138,0.05);
      --nav-surface-2:    rgba(242,210,138,0.09);
      --nav-line:         rgba(242,210,138,0.08);
      --nav-line-str:     rgba(242,210,138,0.16);
      --nav-txt:          #F5EAD0;
      --nav-txt-soft:     rgba(245,234,208,0.68);
      --nav-txt-faint:    rgba(245,234,208,0.42);
      --nav-primary:      #C9A961;
      --nav-primary-2:    #F2D28A;
      --nav-primary-3:    #A88B3D;
      --nav-primary-soft: rgba(201,169,97,0.14);
      --nav-primary-glow: rgba(201,169,97,0.45);
      --nav-shadow:       0 8px 32px -12px rgba(0,0,0,0.7);

      --app-bg:           #0A0A0A;
      --app-bg-2:         #101010;
      --app-txt:          #F5EAD0;
      --app-txt-soft:     rgba(245,234,208,0.68);
      --app-txt-faint:    rgba(245,234,208,0.42);
      --app-line:         rgba(242,210,138,0.08);
      --app-primary:      #C9A961;
      --app-primary-2:    #F2D28A;
    }

    html.theme-light {
      /* ⭐ PURE WHITE */
      --nav-bg:           #FFFFFF;
      --nav-bg-2:         #F9FAFB;
      --nav-panel:        #FFFFFF;
      --nav-panel-2:      #F9FAFB;
      --nav-surface:      rgba(20,20,30,0.04);
      --nav-surface-2:    rgba(20,20,30,0.08);
      --nav-line:         rgba(20,20,30,0.08);
      --nav-line-str:     rgba(20,20,30,0.15);
      --nav-txt:          #1A1613;
      --nav-txt-soft:     rgba(26,22,19,0.62);
      --nav-txt-faint:    rgba(26,22,19,0.42);
      --nav-primary:      #8B6F2A;
      --nav-primary-2:    #A88B3D;
      --nav-primary-3:    #5C1A1B;
      --nav-primary-soft: rgba(139,111,42,0.10);
      --nav-primary-glow: rgba(139,111,42,0.35);
      --nav-shadow:       0 8px 24px -12px rgba(0,0,0,0.12);

      --app-bg:           #FFFFFF;
      --app-bg-2:         #F9FAFB;
      --app-txt:          #1A1613;
      --app-txt-soft:     rgba(26,22,19,0.62);
      --app-txt-faint:    rgba(26,22,19,0.42);
      --app-line:         rgba(20,20,30,0.08);
      --app-primary:      #8B6F2A;
      --app-primary-2:    #A88B3D;
    }

    .app-shell {
      background-color: var(--app-bg, #FFFFFF);
      color: var(--app-txt, #1A1613);
      transition: background-color 0.35s ease, color 0.35s ease;
    }
  `}</style>
);

/* ─── Admin-only route guard ──────────────────────────────────── */
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/signin" replace />;
  const email = (user.email || "").toLowerCase();
  const allowed = ADMIN_EMAILS.map((e) => e.toLowerCase());
  if (!allowed.includes(email)) return <Navigate to="/" replace />;
  return children;
};
const Layout = ({ children }) => {
  const location = useLocation();
  const { user } = useAuth();

  const authPages = [
    "/signin",
    "/signup",
    "/reset-password",
    "/reset-password/verify",
    "/reset-password/update",
  ];
  const isAuthPage = authPages.includes(location.pathname);
  const isChatPage = location.pathname === "/chat";
  const isAdminPage = location.pathname.startsWith("/admin");
  const isAIImagePage = location.pathname.startsWith("/ai-image");
  const isLibraryPage = location.pathname.startsWith("/library");
  const isRemoveBgPage = location.pathname.startsWith("/remove-bg");
  const isAIChatPage = location.pathname.startsWith("/ai-chat");
  const isImageGenPage = location.pathname.startsWith("/image-generator");
  const isShirkatFundPage = location.pathname.startsWith("/shirkat-fund");
  const isApplyPage = location.pathname.startsWith("/apply"); // ⭐ Hide Nav/Footer on Apply

  const isPrivacyPage = location.pathname.startsWith("/privacy");
  const isContactPage = location.pathname.startsWith("/contact");
  const isTermsPage = location.pathname.startsWith("/terms");

  const shouldHideNavbar =
    isAuthPage || isAdminPage || isAIImagePage || isLibraryPage ||
    isRemoveBgPage || isAIChatPage || isImageGenPage ||
    isPrivacyPage || isContactPage || isTermsPage ||
    isShirkatFundPage || isApplyPage;

  const shouldHideFooter =
    !!user ||
    isAuthPage || isChatPage || isAdminPage || isAIImagePage ||
    isLibraryPage || isRemoveBgPage || isAIChatPage || isImageGenPage ||
    isPrivacyPage || isContactPage || isTermsPage ||
    isShirkatFundPage || isApplyPage;

  return (
    <>
      {!shouldHideNavbar && <Navbar />}
      <main
        className={!shouldHideNavbar ? "pt-16 sm:pt-20" : ""}
        style={{ background: "transparent" }}
      >
        {children}
      </main>
      {!shouldHideFooter && <Footer />}
    </>
  );
};
/* ─── Global Onboarding ───────────────────────────────────────── */
const GlobalOnboarding = () => {
  const location = useLocation();
  const HIDDEN_PATHS = [
    "/signin",
    "/signup",
    "/reset-password",
    "/reset-password/verify",
    "/reset-password/update",
    "/apply", // ⭐ Hide on Apply
  ];
  if (location.pathname === "/") return null;
  if (location.pathname.startsWith("/admin")) return null;
  if (location.pathname.startsWith("/ai-image")) return null;
  if (location.pathname.startsWith("/library")) return null;
  if (location.pathname.startsWith("/remove-bg")) return null;
  if (location.pathname.startsWith("/ai-chat")) return null;
  if (location.pathname.startsWith("/image-generator")) return null;
  if (HIDDEN_PATHS.some((p) => location.pathname.startsWith(p))) return null;
  return <OnboardingModal />;
};
const GlobalAIAssistant = () => {
  const location = useLocation();
  const HIDDEN_PATHS = [
    "/signin",
    "/signup",
    "/reset-password",
    "/admin",
    "/ai-image",
    "/image-generator",
    "/library",
    "/remove-bg",
    "/ai-chat",
    "/chat",
    "/study-group-chat",
    "/marketplace-items",
    "/post-ad",
    "/momento",
    "/marketplace-chat",
    "/my-listings",
    "/vehicle",
    "/wallet",
    "/settings",
    "/premium",
    "/shirkat-fund",
    "/apply",
    "/track-application", // ⭐ Add this line here
  ];

  if (HIDDEN_PATHS.some((p) => location.pathname.startsWith(p))) return null;

  return <AIAssistant />;
};

/* ─── Payment notifications listener ─────────────────────────── */
const PaymentListener = () => {
  usePaymentNotifications();
  return null;
};

/* ⭐ Picks Boost vs regular Checkout based on ?boost= query param. */
const SmartCheckout = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const isBoost = params.has("boost");

  if (isBoost) {
    return (
      <ProtectedRoute>
        <BoostCheckout />
      </ProtectedRoute>
    );
  }
  return (
    <ProtectedRoute>
      <Checkout />
    </ProtectedRoute>
  );
};
const GuestAwareChatDock = () => {
  const location = useLocation();
  const { user, loading } = useAuth();

  if (loading) return null;
  if (!user) return null;

  const HIDDEN_PREFIXES = [
    "/marketplace-chat",
    "/chat",
    "/study-group-chat",
    "/signin",
    "/signup",
    "/reset-password",
    "/admin",
    "/ai-chat",
    "/library",
    "/remove-bg",
    "/ai-image",
    "/image-generator",
    "/post-ad",
    "/vehicles",
    "/electronics",
    "/momento",
    "/mobiles",
    "/property",
    "/toy",
    "/contact",
    "/privacy",
    "/settings",
    "/dashboard",
    "/shirkat-fund",
    "/apply",
    "/track-application", // ⭐ Add this line here
  ];

  if (HIDDEN_PREFIXES.some((p) => location.pathname.startsWith(p))) {
    return null;
  }

  return <GlobalChatDock />;
};

/* ⭐ Landing → auto-redirect signed-in users to /marketplace-items */
const SmartLanding = () => {
  const { user, loading } = useAuth();

  if (loading) return null;
  if (user) return <Navigate to="/marketplace-items" replace />;
  return <Landing />;
};

function App() {
  useTheme();

  return (
    <Router>
      <LocaleProvider>
        <AuthProvider>
          <PlanProvider>
            <ProfileProvider>
              <SettingsProvider>
                <TimerProvider>
                  <FilterProvider>
                    <OnboardingProvider>
                      <GlobalThemeStyles />

                      <GlobalToaster />
                      <PaymentListener />

                      <div className="min-h-screen duration-300 app-shell">
                        {/* ⭐ Suspense wraps ROUTES only — floating components load separately */}
                        <Suspense fallback={<PageLoader />}>
                          <Routes>
                            {/* ═══ Auth Pages ═══ */}
                            <Route path="/signin" element={<Layout><SignIn /></Layout>} />
                            <Route path="/signup" element={<Layout><SignUp /></Layout>} />
                            <Route path="/reset-password" element={<Layout><ResetPassword /></Layout>} />
                            <Route path="/reset-password/verify" element={<Layout><ResetPasswordVerify /></Layout>} />
                            <Route path="/reset-password/update" element={<Layout><ResetPasswordUpdate /></Layout>} />
<Route path="/track-application" element={<TrackApplication />} />

                            {/* ═══ ADMIN ═══ */}
                            <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
                            <Route path="/admin/onboarding" element={<AdminRoute><AdminOnboarding /></AdminRoute>} />

                            {/* ═══ AI TOOLS ═══ */}
                            <Route path="/ai-image" element={<AIImageGenerator />} />
                            <Route path="/image-generator" element={<Img_GeneratorPro />} />
                            <Route path="/library" element={<Library />} />
                            <Route path="/remove-bg" element={<RemoveBackground />} />
                            <Route path="/ai-chat" element={<AIChat />} />
                            
                            {/* ⭐ APPLY PAGE */}
                            <Route path="/apply" element={<ApplyPage />} />

                            {/* ═══ Chat pages ═══ */}
                            <Route path="/chat" element={<Layout><ChatPage /></Layout>} />
                            <Route path="/marketplace-chat" element={<Layout><MarketplaceChat /></Layout>} />

                            {/* ═══ Public Pages ═══ */}
                            <Route path="/how-it-works" element={<Layout><HowItWorks /></Layout>} />
                            <Route path="/" element={<SmartLanding />} />
                            <Route path="/home" element={<Layout><Home /></Layout>} />
                            <Route path="/privacy" element={<Layout><PrivacyPage /></Layout>} />
                            <Route path="/contact" element={<Layout><ContactPage /></Layout>} />

                            {/* ═══ Premium ═══ */}
                            <Route path="/premium" element={<Layout><Premium /></Layout>} />
                            <Route path="/subscription" element={<Navigate to="/premium" replace />} />

                            {/* ═══ Vehicles ═══ */}
                            <Route path="/vehicles" element={<Layout><Vehicles /></Layout>} />
                            <Route path="/vehicle/:id" element={<Layout><VehicleDetail /></Layout>} />

                            {/* ═══ Bikes ═══ */}
                            <Route path="/bikes" element={<Layout><Vehicles /></Layout>} />
                            <Route path="/bike/:id" element={<Layout><BikeDetail /></Layout>} />

                            {/* ═══ Mobiles ═══ */}
                            <Route path="/mobiles" element={<Layout><Mobiles /></Layout>} />
                            <Route path="/mobile/:id" element={<Layout><MobileDetail /></Layout>} />

                            {/* ═══ Property ═══ */}
                            <Route path="/property" element={<Layout><Property /></Layout>} />
                            <Route path="/property/:id" element={<Layout><PropertyDetail /></Layout>} />

                            {/* ═══ Electronics ═══ */}
                            <Route path="/electronics" element={<Layout><Electronics /></Layout>} />
                            <Route path="/electronic/:id" element={<Layout><ElectronicsDetail /></Layout>} />

                            {/* ═══ Toys ═══ */}
                            <Route path="/toy/:id" element={<Layout><ToyDetail /></Layout>} />

                            {/* ═══ Cart & Orders ═══ */}
                            <Route path="/cart" element={<Layout><Orders initialTab="cart" /></Layout>} />
                            <Route path="/orders/cart" element={<Layout><Orders initialTab="cart" /></Layout>} />
                            <Route path="/orders" element={<Layout><Orders initialTab="orders" /></Layout>} />
                            <Route path="/orders/list" element={<Layout><Orders initialTab="orders" /></Layout>} />
                            <Route path="/orders/track" element={<Navigate to="/orders" replace />} />

                            {/* ⭐ Checkout ═══ */}
                            <Route path="/checkout" element={<Layout><SmartCheckout /></Layout>} />

                            {/* ⭐ Boost ═══ */}
                            <Route path="/boost" element={
                              <Layout>
                                <ProtectedRoute>
                                  <BoostCheckout />
                                </ProtectedRoute>
                              </Layout>
                            } />

                            {/* ═══ Marketplace Items ═══ */}
                            <Route path="/marketplace-items" element={<Layout><Feed /></Layout>} />
                            <Route path="/feed" element={<Navigate to="/marketplace-items" replace />} />

                            {/* ═══ My Listings ═══ */}
                            <Route path="/my-listings" element={<Layout><MyListings /></Layout>} />

                            {/* ⭐ Listing status ═══ */}
                            <Route path="/listing/:id" element={<Layout><ListingStatusPage /></Layout>} />
                            <Route path="/listing/:id/status" element={<Layout><ListingStatusPage /></Layout>} />

                            {/* ═══ Post Ad ═══ */}
                            <Route path="/post-ad" element={<Layout><PostAd /></Layout>} />

                            {/* ═══ Wallet ═══ */}
                            <Route path="/wallet" element={<Layout><Wallet /></Layout>} />

                            {/* ═══ User Services ═══ */}
                            <Route path="/user-services/:userId" element={<Layout><UserServices /></Layout>} />
                            <Route path="/service/:serviceId" element={<Layout><ServiceDetail /></Layout>} />

                            {/* ═══ Study Groups ═══ */}
                            <Route path="/study-groups" element={<Layout><StudyGroups /></Layout>} />
                            <Route path="/study-groups/:groupId" element={<Layout><GroupDetail /></Layout>} />
                            <Route path="/shirkat-fund" element={<Layout><ShirkatFund /></Layout>} />
                            <Route path="/study-groups/register" element={
                              <Layout>
                                <ProtectedRoute requirePremium={true}>
                                  <StudyPartnerRegister />
                                </ProtectedRoute>
                              </Layout>
                            } />
                            <Route path="/study-group-chat/:groupId" element={
                              <Layout>
                                <ProtectedRoute>
                                  <StudyChat />
                                </ProtectedRoute>
                              </Layout>
                            } />

                            {/* ═══ Momento ═══ */}
                            <Route path="/momento" element={<Layout><Momento /></Layout>} />
                            <Route path="/momento/post/:id" element={<Layout><Momento /></Layout>} />
                            <Route path="/momento/short/:id" element={<Layout><Momento /></Layout>} />
                            <Route path="/momento/video/:id" element={<Layout><Momento /></Layout>} />
                            <Route path="/momento/reel/:id" element={<Layout><Momento /></Layout>} />
                            <Route path="/momento/shorts" element={<Layout><Momento /></Layout>} />
                            <Route path="/momento/shorts/:id" element={<Layout><Momento /></Layout>} />

                            {/* ═══ Settings / Dashboard ═══ */}
                            <Route path="/settings" element={<Layout><SettingsPage /></Layout>} />
                            <Route path="/dashboard" element={<Layout><DashboardPage /></Layout>} />

                            {/* ⭐ Fallback ═══ */}
                            <Route path="*" element={<SmartLanding />} />
                          </Routes>
                        </Suspense>

                        {/* ⭐ Floating components — separate Suspense, null fallback */}
                        <Suspense fallback={null}>
                          <GlobalOnboarding />
                          <PremiumWelcomePopup />
                          <GlobalAIAssistant />
                          <GuestAwareChatDock />
                        </Suspense>
                      </div>
                    </OnboardingProvider>
                  </FilterProvider>
                </TimerProvider>
              </SettingsProvider>
            </ProfileProvider>
          </PlanProvider>
        </AuthProvider>
      </LocaleProvider>
    </Router>
  );
}

export default App;